import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { addToCart } from "store/slices/cartSlice";
import {
  fetchKitById,
  selectSelectedKit,
  selectKitStatus,
  selectKitById,
  setSelectedKit,
  clearSelectedKit,
} from "store/slices/kitsSlice";
import { openModal } from "@/store/slices/authModalSlice";
import { IKitItem } from "@/Interfaces/interface.productsapi";
import toast from "react-hot-toast";
import parse from "html-react-parser";
import he from "he";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import { motion, AnimatePresence } from "framer-motion";
import { selectIsAuthenticated } from "@/store/slices/authSlice";
import ButtonPrimary from "@/shared/Button/ButtonPrimary";
import GoBackBtn from "@/shared/GoBackBtn/GoBackBtn";
import { GlowingBadge } from "@/shared/Badge/GlowingBadge";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  PackageSearch,
  ShoppingCart,
  Mails,
  Package,
} from "lucide-react";
import ToTitleCase from "@/utils/ToTitleCase";
import NcInputNumber from "@/shared/NcInputNumber";
import { useAppDispatch, useAppSelector } from "@/store/hooks/hooks";
import BulkEnquiryDialogue from "@/shared/BulkEnquiryDialogue/BulkEnquiryDialogue";
import { Alert, AlertDescription } from "@/components/UI/alert";
import { openEnquiryDialogue } from "store/slices/enquiryFormSlice";
import { ITEM_TYPES } from "utils/itemTypeHelpers";
import NotifyAddTocart from "../../shared/NotifyAddTocart";
import ProductCard from "@/components/products/ProductCard";

// Reusing ImageGallery Logic (Sticky on desktop only)
const KitImageGallery: React.FC<{ images: string[] }> = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(0);

  return (
    <div className="relative lg:sticky lg:top-28 flex flex-col lg:flex-row gap-4">
      <div
        className={` flex gap-2 order-2 lg:order-1 md:mt-10 lg:mt-0 lg:justify-start overflow-auto
                lg:overflow-x-hidden
                lg:flex-col
                lg:max-h-[440px] 
                max-w-full    
                thin-scrollbar   
                scrollbar-stable
            `}
      >
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => setSelectedImage(index)}
            className={`w-16 h-16 lg:w-full lg:h-20 max-w-20 max-h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0  ${
              selectedImage === index
                ? "border-black"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <img
              src={image}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      <div className="w-full max-w-[300px] md:max-w-[300px] lg:max-w-[400px] order-1 lg:order-2 flex flex-col mx-auto">
        <div className="relative w-full aspect-[3/4] max-h-[320px] lg:max-h-[470px] rounded-lg overflow-hidden bg-white border border-gray-100">
          <button
            onClick={() => setSelectedImage((prev) => Math.max(prev - 1, 0))}
            disabled={selectedImage === 0}
            className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow-xl ${selectedImage === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <ChevronLeft className="text-black font-bold w-5 h-5" />
          </button>

          {images.length > 0 ? (
            <img
              src={images[selectedImage]}
              alt="Main kit view"
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              No Image
            </div>
          )}

          <button
            onClick={() =>
              setSelectedImage((prev) => Math.min(prev + 1, images.length - 1))
            }
            disabled={selectedImage === images.length - 1}
            className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow-xl ${selectedImage === images.length - 1 ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <ChevronRight className="text-black font-bold w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const KitDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const kit = useAppSelector(selectSelectedKit);
  // Select kit from the entity adapter (cache)
  const kitFromStore = useAppSelector((state) =>
    selectKitById(state, id || ""),
  );
  const status = useAppSelector(selectKitStatus);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [quantity, setQuantity] = useState(25);
  const [activeTab, setActiveTab] = useState<"description" | "whatsInside">(
    "whatsInside",
  );
  const [addingToCart, setAddingToCart] = useState(false);
  const [isDialogueOpen, setIsDialogueOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    dispatch(fetchKitById(id));

    return () => {
      dispatch(clearSelectedKit());
    };
  }, [dispatch, id]);

  const displayedKit = kit || kitFromStore;

  // Show full page loader only if we have NO kit data at all and it's loading
  if (status === "loading" && !displayedKit) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <img src={infiniteLoader} alt="loader" className="w-1/5" />
      </div>
    );
  }

  if (!displayedKit && status === "succeeded") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Kit Not Found</h2>
        <Link to="/curated-kits">
          <ButtonPrimary>Back to Kits</ButtonPrimary>
        </Link>
      </div>
    );
  }

  if (!displayedKit) return null;

  const images = (displayedKit.images || [])
    .map((img: any) => (typeof img === "string" ? img : img.imageUrl))
    .filter(Boolean);
  const price = parseFloat(displayedKit.price || "0").toLocaleString("en-IN");
  const kitItems: IKitItem[] = displayedKit.product_group_items || [];

  const notifyAddTocart = () => {
    toast.custom(
      (t) => (
        <NotifyAddTocart
          productImage={images[0]}
          qualitySelected={quantity}
          show={t.visible}
          variantActive={0}
          price={parseFloat(displayedKit.price || "0")}
          name={displayedKit.name}
          onClose={() => toast.dismiss(t.id)}
        />
      ),
      { position: "top-right", id: "nc-product-notify", duration: 3000 },
    );
  };

  const handleAddToCollection = async () => {
    setAddingToCart(true);
    try {
      const cartItem = {
        id: displayedKit.id,
        productId: displayedKit.id,
        variantId: (displayedKit as any).variants?.[0]?.id || "",
        quantity: quantity,
        price: parseFloat(displayedKit.price || "0"),
        name: displayedKit.name,
        images: images as any,
        attributes: {},
        itemType: ITEM_TYPES.PRODUCT_GROUP,
        product_group_items: kitItems,
        productsCount: displayedKit.productsCount || kitItems.length,
      };

      const result = await dispatch(addToCart(cartItem as any));
      if (addToCart.fulfilled.match(result)) {
        notifyAddTocart();
      } else if (addToCart.rejected.match(result)) {
        toast.error(
          (result.payload as string) || "Failed to add to collection",
        );
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setAddingToCart(false);
    }
  };

  const handlePlaceEnquiry = () => {
    if (!isAuthenticated) {
      dispatch(openModal());
      localStorage.setItem(
        "bulkEnquiryIntent",
        JSON.stringify({
          products: [displayedKit.id],
          quantities: { [displayedKit.id]: quantity },
        }),
      );
      return;
    }
    setIsDialogueOpen(true);
  };

  const tabs = [
    { id: "whatsInside", label: "What's Inside" },
    { id: "description", label: "Description" },
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-gray-900 pb-16">
      <Helmet>
        <title>{displayedKit.name} | 360CUSTOMIZER</title>
        <meta name="description" content={displayedKit.description} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <GoBackBtn />
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 lg:mt-8">
        {/* Top Section: Grid for Gallery and Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-16">
          {/* Left: Gallery */}
          <div className="w-full">
            <KitImageGallery images={images} />
          </div>

          {/* Right: Info & Actions */}
          <div className="space-y-6">
            {/* Title & Badges */}
            <div>
              <div className="flex flex-wrap gap-2 mb-3 items-center">
                <GlowingBadge
                  icon={Package}
                  text="Curated Kit"
                  bgColor="bg-blue-500"
                  textColor="text-white"
                  className="bg-blue-50 text-blue-800"
                  showGlow
                />
                {kitItems.length > 0 && (
                  <span className="text-xs font-medium text-blue-500 bg-blue-50 px-2 py-1 rounded-md border border-blue-100">
                    {kitItems.length} Products inside kit
                  </span>
                )}
              </div>

              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Brand:</span>
                  <span>360Customizer</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Type:</span>
                  <span className="capitalize">
                    {displayedKit.product_group_type?.name?.replace("_", " ") ||
                      "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight break-words">
                <ToTitleCase text={displayedKit.name} />
              </h1>
            </div>

            {/* Price */}
            <div className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <div className="flex items-baseline gap-2">
                ₹{price}
                <span className="text-lg text-gray-500 font-medium">/ kit</span>
              </div>
            </div>

            {/* Quantity */}
            <div className="w-full md:w-40">
              <NcInputNumber
                defaultValue={quantity}
                onChange={setQuantity}
                min={25}
                size="lg"
              />
            </div>

            <Alert className="mt-4 border-blue-200 bg-blue-50">
              <AlertDescription className="text-blue-600">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-500" />
                  <span>
                    Listed prices are exclusive of{" "}
                    <strong className="font-semibold">
                      GST, Branding, & Delivery
                    </strong>{" "}
                    costs
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-500" />
                  <span>
                    Additional{" "}
                    <strong className="font-semibold">discount</strong> would be
                    applied varied on the kit quantity purchased
                  </span>
                </p>
              </AlertDescription>
            </Alert>
            <Alert className="mt-4 border-green-200 bg-green-50">
              <AlertDescription className="text-green-600">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
                  <span>
                    Add kit to the collection to generate catalog or use any
                    tool
                  </span>
                </p>
              </AlertDescription>
            </Alert>

            {/* Actions: "Add to Collection" & "Place Enquiry" */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <ButtonPrimary
                onClick={handleAddToCollection}
                disabled={addingToCart}
                loading={addingToCart}
                className="w-full md:w-40 xl:w-1/3 bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Add To Collection
              </ButtonPrimary>
              <ButtonPrimary
                onClick={handlePlaceEnquiry}
                className="w-full md:w-40 xl:w-1/3 bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Mails className="w-5 h-5" />
                Place Enquiry
              </ButtonPrimary>
            </div>

            <div className="border-t pt-6 relative">
              {isDialogueOpen && (
                <BulkEnquiryDialogue
                  onClose={() => {
                    setIsDialogueOpen(false);
                    dispatch(
                      openEnquiryDialogue({ products: [], quantities: {} }),
                    );
                  }}
                  products={[
                    {
                      id: displayedKit.id,
                      productId: displayedKit.id,
                      variantId: (displayedKit as any).variants?.[0]?.id || "",
                      qty: quantity,
                      price: parseFloat(displayedKit.price || "0"),
                      name: displayedKit.name,
                      image: images[0] || "",
                      images: displayedKit.images as any,
                      category: displayedKit.categoryName || "",
                      brand: displayedKit.brandName || "360Customizer",
                      description: displayedKit.description,
                      variants: (displayedKit as any).variants || [],
                      itemType: ITEM_TYPES.PRODUCT_GROUP,
                      product_group_items: displayedKit.product_group_items,
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section: Full Width Tabs for Description & Contents */}
        <div className="border-t pt-10">
          {/* Tabs Navigation */}
          <div className="flex justify-start flex-wrap gap-2 sm:gap-8 mb-8 border-b border-gray-200">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                whileTap={{ scale: 0.95 }}
                className={`relative px-1 py-4 text-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? "text-indigo-600"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="underline"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"
                  />
                )}
              </motion.button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[300px]">
            <AnimatePresence mode="wait">
              {activeTab === "description" && (
                <motion.div
                  key="description"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="max-w-4xl"
                >
                  <div className="prose prose-lg text-gray-600 leading-relaxed">
                    {displayedKit.description
                      ? parse(he.decode(displayedKit.description))
                      : "No description available."}
                  </div>
                </motion.div>
              )}

              {activeTab === "whatsInside" && (
                <motion.div
                  key="whatsInside"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {status === "loading" ? (
                    <div className="min-h-screen flex justify-center items-center">
                      <img
                        src={infiniteLoader}
                        alt="loader"
                        className="w-1/5"
                      />
                    </div>
                  ) : kitItems.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {kitItems.map((item: IKitItem) => (
                        <div key={item.id} className="min-w-0 w-full">
                          <ProductCard
                            data={item}
                            dispatch={dispatch}
                            addToCart={addToCart}
                            showViewDetails={true}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-2xl">
                      <PackageSearch className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Kit contents not listed.</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KitDetailsPage;
